import React from "react";
import { graphql, useFragment } from "react-relay";

import { faSparkles } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faExpandAlt, faSpinner, faSparkles as fasSparkles } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { differenceInMilliseconds } from "date-fns";

import { AppContext } from "~/common";
import useMedia from "~/lib/hooks/useMedia";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { useTrackAskCopilotOpened } from "~/meetingRecorder/AskCopilotPanel";
import { useOpenAskCopilot } from "~/stream/SuggestedActions/AskCopilot";

import { BillingContext } from "../account/Billing/BillingContext";
import { SummaryText } from "./SummaryText";
import {
	formatDate,
	formatTimestamp,
	useAutoRefetchMeetingRecordings,
	useIsNarrowViewport,
	useLongestMeetingRecording,
	useNoteHasRecordings,
} from "./utils";

import type { MeetingSummaryBanner_IfMeetingSummaryBannerHidden_note$key } from "~/__generated__/MeetingSummaryBanner_IfMeetingSummaryBannerHidden_note.graphql";
import type { MeetingSummaryBanner_note$key } from "~/__generated__/MeetingSummaryBanner_note.graphql";
import type { MeetingSummaryBanner_recording$key } from "~/__generated__/MeetingSummaryBanner_recording.graphql";
import type { MeetingSummaryBanner_useShouldShowMeetingSummaryBanner_note$key } from "~/__generated__/MeetingSummaryBanner_useShouldShowMeetingSummaryBanner_note.graphql";

import styles from "./css/MeetingSummaryBanner.module.css";

function RealMeetingSummaryBanner({
	noteId,
	recording: recordingKey,
	onOpenTranscriptModal,
}: {
	noteId: string;
	recording: MeetingSummaryBanner_recording$key;
	onOpenTranscriptModal: () => void;
}) {
	const { analyticsFingerprint } = React.useContext(BillingContext);
	const recording = useFragment(
		graphql`
			fragment MeetingSummaryBanner_recording on MeetingRecording {
				meetingCallStart
				meetingCallEnd
				meetingRecordedVideoDuration
				meetingRecordedVideoPosterUrl
				recordingDataAutoDeletionDate
				summaryWillBeDeleted
				activeSummary {
					chapters {
						bulletPoints {
							text
							timestamp
							textShort
							context
						}
						timestamp
						title
					}
					summary
				}
				hasAskCopilotConversation
			}
		`,
		recordingKey,
	);

	const onClickSummaryText = React.useCallback(
		(e: React.MouseEvent) => {
			const selection = window.getSelection();

			if (selection?.containsNode(e.currentTarget, true) && String(selection)) {
				// The user selected some text, so we don't want to open the
				// transcript modal.
				return;
			}

			onOpenTranscriptModal();
			dispatchAnalyticsTrack("[AI Copilot] Summary Banner Clicked", { fingerprint: analyticsFingerprint });
		},
		[onOpenTranscriptModal, analyticsFingerprint],
	);

	const trackAskCopilotOpened = useTrackAskCopilotOpened();
	const openAskCopilot = useOpenAskCopilot(noteId);
	const onClickAskCopilotButton = React.useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation();
			openAskCopilot();
			trackAskCopilotOpened("Summary banner", {
				// eslint-disable-next-line camelcase
				note_id: noteId,
			});
		},
		[noteId, openAskCopilot, trackAskCopilotOpened],
	);

	const isNarrowViewport = useIsNarrowViewport();
	const latestChapterTimestamp = Math.max(
		...(recording.activeSummary?.chapters?.map(chapter => chapter.timestamp ?? 0) ?? []),
	);
	const hasChapters = recording.activeSummary?.chapters !== null;
	const isPrinting = useMedia("print");
	const bulletPointLimit = isPrinting ? undefined : isNarrowViewport ? (hasChapters ? 5 : 8) : 3;

	const formattedDuration = recording.meetingRecordedVideoDuration
		? formatDuration(recording.meetingRecordedVideoDuration * 1000)
		: recording.meetingCallEnd
			? formatDuration(
					differenceInMilliseconds(new Date(recording.meetingCallEnd), new Date(recording.meetingCallStart)),
				)
			: null;

	const { account, user } = React.useContext(AppContext);
	const askCopilotEnabled = (account?.allowAiFeatures || recording.hasAskCopilotConversation) && !user?.isGuest;
	const expandButton = askCopilotEnabled ? (
		<button className={styles.askCopilotButton} onClick={onClickAskCopilotButton}>
			<FontAwesomeIcon icon={fasSparkles} className={styles.askCopilotButtonSparkles} />
			Ask Fellow
		</button>
	) : (
		<button className={styles.expandButton}>
			<FontAwesomeIcon icon={faExpandAlt} />
		</button>
	);

	return (
		<section
			onClick={onClickSummaryText}
			className={cx(styles.summaryBanner, {
				[styles.hasChapters]: hasChapters,
			})}
		>
			<div className={styles.summaryContainer}>
				<div>
					<h1 className={styles.heading}>
						<FontAwesomeIcon icon={faSparkles} className={styles.sparklesIcon} />
						Meeting Recap
						<small className={styles.addedOrDeletionDate}>
							{recording.summaryWillBeDeleted && recording.recordingDataAutoDeletionDate
								? `Available until ${formatDate(new Date(recording.recordingDataAutoDeletionDate))}`
								: recording.meetingCallEnd
									? `Added ${formatDate(new Date(recording.meetingCallEnd))}`
									: null}
						</small>
					</h1>
					{recording.activeSummary?.summary && (
						<SummaryText bulletPointLimit={bulletPointLimit} className={styles.summaryText}>
							{recording.activeSummary.summary}
						</SummaryText>
					)}
				</div>
				{hasChapters ? (
					<div className={styles.chaptersContainer}>
						<div>
							<h2 className={styles.chaptersHeading}>Chapters</h2>
							<table className={styles.chaptersTable}>
								<tbody>
									{(recording.activeSummary?.chapters ?? []).slice(0, isNarrowViewport ? 4 : 2).map(chapter => (
										<tr key={chapter.timestamp} className={styles.chaptersTableRow}>
											<td className={styles.chaptersTableRowTimestamp}>
												{chapter.timestamp && formatTimestamp(chapter.timestamp, latestChapterTimestamp)}
											</td>
											<td className={styles.chaptersTableRowTitle}>{chapter.title}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
						{expandButton}
					</div>
				) : (
					expandButton
				)}
			</div>
			{recording.meetingRecordedVideoPosterUrl && (
				<div
					style={{
						background: [
							"no-repeat center/contain linear-gradient(180deg, rgba(89, 89, 89, 0) 0%, rgba(0, 0, 0, 0.6) 100%)",
							`no-repeat center/contain url("${recording.meetingRecordedVideoPosterUrl.replace(/"/g, '\\"')}")`,
						].join(", "),
					}}
					className={styles.videoPoster}
				>
					<PlayButton color="rgba(255, 255, 255, 0.75)" className={styles.videoPosterOverlayPlayButton} />
					{formattedDuration && <span className={styles.videoPosterOverlayDuration}>{formattedDuration}</span>}
				</div>
			)}
		</section>
	);
}

const shortMinuteFormatter = new Intl.NumberFormat(undefined, {
	style: "unit",
	unit: "minute",
	unitDisplay: "short",
	maximumFractionDigits: 0,
});
const narrowHourFormatter = new Intl.NumberFormat(undefined, {
	style: "unit",
	unit: "hour",
	unitDisplay: "narrow",
	maximumFractionDigits: 0,
});
const narrowMinuteFormatter = new Intl.NumberFormat(undefined, {
	style: "unit",
	unit: "minute",
	unitDisplay: "narrow",
	maximumFractionDigits: 0,
});

const ONE_MINUTE_MS = 60000;
const ONE_HOUR_MS = ONE_MINUTE_MS * 60;

function formatDuration(durationMs: number): string {
	const haveHours = durationMs >= ONE_HOUR_MS;
	const minutes = (durationMs % ONE_HOUR_MS) / ONE_MINUTE_MS;

	if (haveHours) {
		const formattedHours = narrowHourFormatter.format(Math.floor(durationMs / ONE_HOUR_MS)),
			formattedMinutes = narrowMinuteFormatter.format(minutes);
		return `${formattedHours} ${formattedMinutes}`;
	}
	return shortMinuteFormatter.format(minutes);
}

export function PlayButton({
	color,
	className,
	width = "21",
	height = "24",
}: {
	color: string;
	className?: string;
	width?: string;
	height?: string;
}) {
	return (
		<svg
			width={width}
			height={height}
			viewBox="0 0 21 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			className={className}
		>
			<path
				// eslint-disable-next-line max-len
				d="M19.8618 10.0499C21.314 10.94 21.314 13.0479 19.8618 13.938L3.37276 23.6815C1.87376 24.5715 0 23.4941 0 21.7141V2.22699C0 0.306386 2.01429 -0.489961 3.37276 0.306386L19.8618 10.0499Z"
				fill={color}
			/>
		</svg>
	);
}

function MeetingSummaryLoadingBanner() {
	return (
		<section className={cx(styles.summaryBanner, styles.hasChapters, styles.loadingBanner)}>
			<div className={styles.summaryContainer}>
				<h1 className={styles.heading}>
					<FontAwesomeIcon icon={faSpinner} pulse className={styles.sparklesIcon} />
					Generating meeting recap…
				</h1>
				<div className={styles.skeletonSummary}>
					<div />
					<div />
					<div />
				</div>

				<div className={styles.skeletonChapters}>
					<div>
						<span></span>
						<span></span>
					</div>
					<div>
						<span></span>
						<span></span>
					</div>
				</div>
			</div>
			<div className={styles.skeletonVideo}>
				<PlayButton color="var(--veryMutedRoyal)" />
			</div>
		</section>
	);
}

export function MeetingSummaryBanner({
	noteId,
	note: noteKey,
	onOpenTranscriptModal,
}: {
	noteId: string;
	note: MeetingSummaryBanner_note$key;
	onOpenTranscriptModal: () => void;
}) {
	const note = useFragment(
		graphql`
			fragment MeetingSummaryBanner_note on Note {
				id
				groupName
				meetingRecordings {
					id
					transcript
					activeSummary {
						summary
					}
					meetingCallEnd
					summaryWillBeDeleted
					recordingDataAutoDeletionDate
					...utils_useLongestMeetingRecording_meetingRecordings
					...MeetingSummaryBanner_recording
				}
				...MeetingSummaryBanner_useShouldShowMeetingSummaryBanner_note
			}
		`,
		noteKey,
	);

	const longestMeetingRecording = useLongestMeetingRecording(note.meetingRecordings);

	const shouldShowMeetingSummaryBanner = useShouldShowMeetingSummaryBanner(note);
	if (!shouldShowMeetingSummaryBanner) return null;

	if (longestMeetingRecording?.activeSummary?.summary != null) {
		return (
			<RealMeetingSummaryBanner
				noteId={noteId}
				recording={longestMeetingRecording}
				onOpenTranscriptModal={onOpenTranscriptModal}
			/>
		);
	} else if (
		longestMeetingRecording &&
		// Show the loading banner if transcription isn't done yet, or if we have
		// the transcript but no summary and we expect there to be a summary
		// (because the transcript is non-empty)
		(!longestMeetingRecording.transcript || longestMeetingRecording.transcript.length)
	) {
		return <MeetingSummaryLoadingBanner />;
	} else {
		// Either there are no recordings, or the longest recording doesn't have
		// any transcript/summary.
		return null;
	}
}

export function useShouldShowMeetingSummaryBanner(
	noteKey: MeetingSummaryBanner_useShouldShowMeetingSummaryBanner_note$key,
): boolean {
	const note = useFragment(
		graphql`
			fragment MeetingSummaryBanner_useShouldShowMeetingSummaryBanner_note on Note {
				id
				...utils_useNoteHasRecordings_note
				...utils_useAutoRefetchMeetingRecording_note
			}
		`,
		noteKey,
	);

	useAutoRefetchMeetingRecordings(note);

	return useNoteHasRecordings(note);
}

export function IfMeetingSummaryBannerHidden({
	note: noteKey,
	children,
	onVisibilityChange,
}: React.PropsWithChildren<{
	note: MeetingSummaryBanner_IfMeetingSummaryBannerHidden_note$key;
	onVisibilityChange: (visible: boolean) => void;
}>) {
	const note = useFragment(
		graphql`
			fragment MeetingSummaryBanner_IfMeetingSummaryBannerHidden_note on Note {
				...MeetingSummaryBanner_useShouldShowMeetingSummaryBanner_note
			}
		`,
		noteKey,
	);

	const showBanner = useShouldShowMeetingSummaryBanner(note);

	React.useEffect(() => {
		onVisibilityChange(showBanner);
	}, [onVisibilityChange, showBanner]);

	if (showBanner) {
		return null;
	}
	return <>{children}</>;
}
